/**
 * Canonical industry verticals — ONE source of truth for the whole stack.
 *
 * XOP's market is energy + critical-infrastructure + life-safety. The SALES
 * AGENT owns the roster (`xop-agents/apps/sales/app/services/verticals.py`);
 * this module mirrors it for TypeScript (CRM forms + Email Campaigns audience
 * builder) and crm-api mirrors it for the backfill. Historically the CRM kept a
 * SEPARATE, collapsed list ("Oil & Gas" fused oil+gas, "Power & Utilities" fused
 * power+wind) that diverged from the agent — this replaces it, so customers,
 * prospects, contacts, the agent, and campaigns all speak the same vocabulary.
 *
 * `slug` is the stable machine value (matches the agent + VerticalBadge). `label`
 * is the human-readable display string (mirrors
 * `apps/web/src/pages/sales/components/VerticalBadge.tsx`). **Records store the
 * LABEL on `industry`** (human-readable, keeps `by_industry` / `/contacts/industries`
 * working), and `canonicalizeIndustry()` folds any free text onto a canonical
 * label. Order mirrors the agent dropdown (the sixteen PRIMARY verticals first,
 * then the light tail).
 *
 * **2026-08-30 — airports and military bases split the same way energy did.**
 * "Airports" and "air force bases" were never one sales motion each: a domestic
 * regional field, an international hub, a state-run Air National Guard wing, a
 * US Air Force / Navy installation and an allied air force's airfield are bought
 * by different people on different money. `aviation` and `defence_military`
 * SURVIVE as the RESIDUAL PARENTS (airlines / ATC; ministries, commands,
 * primes) and move to the tail — they are still canonical, so every existing
 * row stays valid. What changed is the FOLD: a phrase naming a specific facility
 * type ("regional airport", "Royal Air Force", "naval station") lands on the
 * granular slug, while a bare "airport" / "air force" keeps the parent rather
 * than guessing domestic-vs-international or US-vs-allied. Three sectors that
 * had no home joined at the same time: private-wire carriers, prisons, VA homes.
 */

export interface Vertical {
  slug: string;
  label: string;
}

/**
 * The canonical verticals, in display order: the sixteen PRIMARY verticals the
 * agent hunts, then the light tail (Unclassified is appended by
 * `VERTICAL_LABELS`, not listed here).
 */
export const VERTICALS: readonly Vertical[] = [
  // ── Primary — what the agent hunts and the harvester sweeps ──
  { slug: "airports_domestic", label: "Domestic Airports" },
  { slug: "airports_intl", label: "Int'l Airports" },
  { slug: "nuclear", label: "Nuclear" },
  { slug: "oil", label: "Oil" },
  { slug: "gas", label: "Gas / LNG" },
  { slug: "wind", label: "Wind & Renewables" },
  { slug: "power", label: "Power & Utilities" },
  { slug: "chemical", label: "Chemical" },
  { slug: "healthcare", label: "Hospitals / Healthcare" },
  { slug: "va_homes", label: "VA Homes" },
  { slug: "public_safety", label: "Public Safety" },
  { slug: "corrections", label: "Prisons / Corrections" },
  { slug: "air_national_guard", label: "Air National Guard" },
  { slug: "military_bases_us", label: "USAF & Naval Bases" },
  { slug: "air_force_bases_intl", label: "Int'l Air Force Bases" },
  { slug: "private_wire_carrier", label: "Private Wire Carriers" },
  // ── Light tail. `aviation` + `defence_military` lead it as the residual
  //    PARENTS of the 2026-08-30 splits: an AIRLINE is Aviation, an AIRPORT is
  //    one of the two above; a defence MINISTRY is Defence / Military, a BASE
  //    is one of the three above. `defence_military` was renamed off
  //    "Air Force / Defence" so it can't be mistaken for a base vertical.
  { slug: "aviation", label: "Aviation" },
  { slug: "defence_military", label: "Defence / Military" },
  { slug: "hospitality", label: "Hotels / Hospitality" },
  { slug: "fintech_trader_voice", label: "Trader Voice" },
  { slug: "government", label: "Government" },
  { slug: "education", label: "Education" },
  { slug: "transportation", label: "Transportation" },
  { slug: "telco_carrier", label: "Telco / Carrier" },
  { slug: "corporate", label: "Corporate" },
] as const;

/** The catch-all when free text maps to nothing on the roster. */
export const UNCLASSIFIED_LABEL = "Unclassified";
export const UNCLASSIFIED_SLUG = "unknown";

/** Ordered labels for a `<Select>` / facet list (Unclassified appended last). */
export const VERTICAL_LABELS: readonly string[] = [
  ...VERTICALS.map((v) => v.label),
  UNCLASSIFIED_LABEL,
];

const LABEL_BY_SLUG = new Map<string, string>(VERTICALS.map((v) => [v.slug, v.label]));

/** Human label for a canonical slug (Unclassified for `unknown`/off-roster). */
export function labelForVerticalSlug(slug: string | null | undefined): string {
  if (!slug) return UNCLASSIFIED_LABEL;
  return LABEL_BY_SLUG.get(slug.trim().toLowerCase()) ?? UNCLASSIFIED_LABEL;
}

// ── free-text → canonical slug (ported from the agent's verticals.py) ──────
// Lowercase, strip, collapse every non-alphanumeric run (incl. slug underscores)
// to one space — so "Oil_And_Gas", "oil & gas", "OIL AND GAS" reduce alike.
function norm(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const SLUG_BY_NORMAL = new Map<string, string>(
  VERTICALS.map((v) => [norm(v.slug), v.slug]),
);
// Also let the normalized LABEL round-trip to its slug ("power utilities" → power).
for (const v of VERTICALS) SLUG_BY_NORMAL.set(norm(v.label), v.slug);

// Exact normalized phrase → slug (higher precision than the keyword scan).
const ALIASES: Record<string, string> = {
  // airports_intl / airports_domestic (split out of aviation 2026-08-30).
  // `norm` turns "Int'l" into "int l", so that spelling is listed too.
  "international airport": "airports_intl", "intl airport": "airports_intl",
  "intl airports": "airports_intl", "int l airport": "airports_intl",
  "int l airports": "airports_intl",
  "commercial airport": "airports_domestic", "commercial airports": "airports_domestic",
  "domestic airport": "airports_domestic", "domestic airports": "airports_domestic",
  "regional airport": "airports_domestic", "regional airports": "airports_domestic",
  "municipal airport": "airports_domestic", "municipal airports": "airports_domestic",
  "general aviation airport": "airports_domestic",
  // aviation — the RESIDUAL parent. A bare "airport" stays here on purpose:
  // domestic-vs-international is a fact the text has to state, never a guess.
  airports: "aviation", airport: "aviation", airline: "aviation", airlines: "aviation",
  "air navigation": "aviation", "air traffic": "aviation", "air traffic control": "aviation",
  atc: "aviation", ansp: "aviation", airfield: "aviation", airfields: "aviation",
  nuclear: "nuclear", "nuclear power": "nuclear", "nuclear power plant": "nuclear",
  "nuclear plant": "nuclear", "nuclear reactor": "nuclear", reactor: "nuclear", npp: "nuclear",
  oil: "oil", "oil and gas": "oil", "oil gas": "oil", petroleum: "oil", refinery: "oil",
  refineries: "oil", refining: "oil", crude: "oil", "crude oil": "oil", upstream: "oil",
  downstream: "oil", offshore: "oil", "offshore platform": "oil", "oil pipeline": "oil",
  gas: "gas", lng: "gas", "natural gas": "gas", "lng terminal": "gas", "gas terminal": "gas",
  "gas processing": "gas", "gas pipeline": "gas", "gas plant": "gas", "gas field": "gas", midstream: "gas",
  wind: "wind", "wind farm": "wind", "wind farms": "wind", "offshore wind": "wind",
  renewable: "wind", renewables: "wind", "renewable energy": "wind", solar: "wind",
  "solar farm": "wind", photovoltaic: "wind", "clean energy": "wind",
  power: "power", "power generation": "power", "power plant": "power", "power station": "power",
  "power stations": "power", utility: "power", utilities: "power", "electric utility": "power",
  grid: "power", "energy utility": "power", hydro: "power", hydroelectric: "power",
  hydropower: "power", geothermal: "power", "thermal power": "power", "coal power": "power",
  transmission: "power", electricity: "power", tso: "power", dso: "power",
  chemical: "chemical", chemicals: "chemical", "chemical plant": "chemical",
  petrochemical: "chemical", petrochem: "chemical", industrial: "chemical", manufacturing: "chemical",
  hospital: "healthcare", hospitals: "healthcare", health: "healthcare",
  "health system": "healthcare", "health systems": "healthcare", medical: "healthcare",
  "medical center": "healthcare", clinic: "healthcare",
  // va_homes — ahead of healthcare in the keyword scan so a "VA Medical Center"
  // isn't swallowed by the generic "medical".
  "va home": "va_homes", "va homes": "va_homes", "veterans home": "va_homes",
  "veterans homes": "va_homes", "veteran home": "va_homes",
  "state veterans home": "va_homes", "state veterans homes": "va_homes",
  "veterans affairs": "va_homes", "department of veterans affairs": "va_homes",
  "veterans administration": "va_homes", "va medical center": "va_homes",
  "va medical centre": "va_homes", "veterans affairs medical center": "va_homes",
  "va hospital": "va_homes", vamc: "va_homes",
  // corrections
  prison: "corrections", prisons: "corrections", jail: "corrections",
  jails: "corrections", corrections: "corrections", correctional: "corrections",
  "correctional facility": "corrections", "correctional facilities": "corrections",
  "correctional institution": "corrections", "department of corrections": "corrections",
  penitentiary: "corrections", "state prison": "corrections",
  "federal prison": "corrections", "detention center": "corrections",
  "detention centre": "corrections", "detention facility": "corrections",
  // air_national_guard / military_bases_us / air_force_bases_intl
  // (split out of defence_military 2026-08-30)
  "air national guard": "air_national_guard", "national guard": "air_national_guard",
  "air national guard base": "air_national_guard", ang: "air_national_guard",
  "ang base": "air_national_guard", "air guard": "air_national_guard",
  "national guard base": "air_national_guard",
  "us air force": "military_bases_us", "us air force base": "military_bases_us",
  usaf: "military_bases_us", "usaf base": "military_bases_us",
  afb: "military_bases_us", "naval base": "military_bases_us",
  "naval station": "military_bases_us", "navy base": "military_bases_us",
  "us navy": "military_bases_us", "marine corps base": "military_bases_us",
  "joint base": "military_bases_us", "military base": "military_bases_us",
  "military bases": "military_bases_us",
  "international air force base": "air_force_bases_intl",
  "intl air force base": "air_force_bases_intl",
  "int l air force base": "air_force_bases_intl",
  "int l airforce base": "air_force_bases_intl",
  "royal air force": "air_force_bases_intl", raf: "air_force_bases_intl",
  "raf base": "air_force_bases_intl", "nato air base": "air_force_bases_intl",
  "nato airbase": "air_force_bases_intl", "allied air force base": "air_force_bases_intl",
  "foreign air force base": "air_force_bases_intl",
  // defence_military — the RESIDUAL parent. A bare "air force" stays here:
  // US-vs-allied is a fact the text has to state, never a guess. The OLD stored
  // label "Air Force / Defence" folds home too (renamed 2026-08-30).
  defense: "defence_military", defence: "defence_military", military: "defence_military",
  "air force": "defence_military", "air force base": "defence_military",
  "armed forces": "defence_military", navy: "defence_military", army: "defence_military",
  nato: "defence_military", "air force defence": "defence_military",
  hotel: "hospitality", hotels: "hospitality", resort: "hospitality", resorts: "hospitality",
  casino: "hospitality", "casino resort": "hospitality", lodging: "hospitality", hospitality: "hospitality",
  "trader voice": "fintech_trader_voice", "financial services": "fintech_trader_voice",
  fintech: "fintech_trader_voice", finance: "fintech_trader_voice", bank: "fintech_trader_voice",
  banking: "fintech_trader_voice", "investment bank": "fintech_trader_voice", trading: "fintech_trader_voice",
  "public safety": "public_safety", "911": "public_safety", fire: "public_safety",
  "fire department": "public_safety", ems: "public_safety", police: "public_safety",
  "emergency services": "public_safety", "first responder": "public_safety",
  psap: "public_safety", dispatch: "public_safety",
  // The old CRM bucket "Government & Public Safety" is ambiguous; default the
  // combined label to government (the broader bucket) rather than let the keyword
  // scan pick public_safety off the "public safety" substring.
  "government public safety": "government",
  government: "government", "government agency": "government", municipal: "government",
  municipality: "government", city: "government", county: "government", federal: "government",
  "state government": "government", "public sector": "government",
  education: "education", university: "education", universities: "education", college: "education",
  school: "education", "school district": "education", campus: "education", academic: "education",
  transportation: "transportation", transport: "transportation", rail: "transportation",
  railway: "transportation", railroad: "transportation", port: "transportation",
  ports: "transportation", "port authority": "transportation", transit: "transportation",
  metro: "transportation", logistics: "transportation",
  // private_wire_carrier — ahead of telco_carrier in the keyword scan too, so
  // "private wire carrier" doesn't collapse onto the generic "carrier".
  "private wire": "private_wire_carrier", "private wire carrier": "private_wire_carrier",
  "private wire carriers": "private_wire_carrier", "private line": "private_wire_carrier",
  "private line carrier": "private_wire_carrier", "private line carriers": "private_wire_carrier",
  "private circuit": "private_wire_carrier", "private circuits": "private_wire_carrier",
  "dedicated line carrier": "private_wire_carrier",
  telco: "telco_carrier", telecom: "telco_carrier", telecoms: "telco_carrier",
  carrier: "telco_carrier", telecommunications: "telco_carrier", "mobile operator": "telco_carrier",
  isp: "telco_carrier",
  corporate: "corporate", enterprise: "corporate", company: "corporate", business: "corporate",
  commercial: "corporate",
};

// Ordered keyword → slug substring fallback (first hit wins; granular energy
// keywords precede the generic "power" so "nuclear power plant" → nuclear).
const KEYWORDS: ReadonlyArray<readonly [string, string]> = [
  // Every QUALIFIED form sits above the generic one it would be swallowed by.
  ["international airport", "airports_intl"], ["intl airport", "airports_intl"],
  ["int l airport", "airports_intl"],
  ["commercial airport", "airports_domestic"], ["domestic airport", "airports_domestic"],
  ["regional airport", "airports_domestic"], ["municipal airport", "airports_domestic"],
  ["airport", "aviation"], ["aviation", "aviation"], ["airline", "aviation"],
  ["nuclear", "nuclear"], ["reactor", "nuclear"],
  ["refiner", "oil"], ["crude", "oil"], ["petroleum", "oil"],
  ["lng", "gas"], ["natural gas", "gas"], ["gas pipeline", "gas"], ["gas process", "gas"], ["gas terminal", "gas"],
  ["wind", "wind"], ["solar", "wind"], ["renewable", "wind"], ["photovoltaic", "wind"],
  ["hydro", "power"], ["geothermal", "power"], ["power station", "power"], ["power plant", "power"],
  ["utilit", "power"], ["grid", "power"], ["transmission", "power"], ["electric", "power"],
  ["oil", "oil"], [" gas", "gas"], ["power", "power"],
  ["petrochem", "chemical"], ["chemical", "chemical"],
  ["veterans home", "va_homes"], ["veterans affairs", "va_homes"],
  ["veterans administration", "va_homes"], ["va medical", "va_homes"],
  ["va hospital", "va_homes"], ["va home", "va_homes"],
  ["hospital", "healthcare"], ["health", "healthcare"], ["medical", "healthcare"],
  ["hotel", "hospitality"], ["resort", "hospitality"], ["casino", "hospitality"],
  ["air national guard", "air_national_guard"], ["national guard", "air_national_guard"],
  ["royal air force", "air_force_bases_intl"], ["nato air", "air_force_bases_intl"],
  ["international air force", "air_force_bases_intl"],
  ["us air force", "military_bases_us"], ["usaf", "military_bases_us"],
  [" afb", "military_bases_us"], ["naval base", "military_bases_us"],
  ["naval station", "military_bases_us"], ["navy base", "military_bases_us"],
  ["marine corps", "military_bases_us"], ["joint base", "military_bases_us"],
  ["military base", "military_bases_us"],
  ["air force", "defence_military"], ["defen", "defence_military"], ["military", "defence_military"],
  ["trader voice", "fintech_trader_voice"], ["bank", "fintech_trader_voice"], ["financ", "fintech_trader_voice"],
  ["prison", "corrections"], ["jail", "corrections"], ["correction", "corrections"],
  ["penitentiary", "corrections"], ["detention", "corrections"],
  ["public safety", "public_safety"], ["911", "public_safety"], ["fire", "public_safety"], ["police", "public_safety"], ["psap", "public_safety"],
  ["government", "government"], ["municipal", "government"], ["federal", "government"],
  ["universit", "education"], ["college", "education"], ["school", "education"],
  ["rail", "transportation"], ["port", "transportation"], ["transit", "transportation"],
  ["private wire", "private_wire_carrier"], ["private line", "private_wire_carrier"],
  ["private circuit", "private_wire_carrier"],
  ["telco", "telco_carrier"], ["telecom", "telco_carrier"], ["carrier", "telco_carrier"],
  ["enterprise", "corporate"], ["corporate", "corporate"],
];

// Legacy lumped spellings re-split to the broad bucket (deterministic fold).
const LEGACY_LUMPED: Record<string, string> = {
  "oil and gas": "oil", "oil gas": "oil",
  "power generation": "power", "power gen": "power",
};

/** Fold free text onto a canonical vertical SLUG (`unknown` when unmappable). */
export function canonicalizeVerticalSlug(value: string | null | undefined): string {
  if (!value) return UNCLASSIFIED_SLUG;
  const key = norm(value);
  if (!key) return UNCLASSIFIED_SLUG;
  if (LEGACY_LUMPED[key]) return LEGACY_LUMPED[key];
  if (SLUG_BY_NORMAL.has(key)) return SLUG_BY_NORMAL.get(key)!;
  if (ALIASES[key]) return ALIASES[key];
  for (const [keyword, slug] of KEYWORDS) {
    if (key.includes(keyword)) return slug;
  }
  return UNCLASSIFIED_SLUG;
}

/**
 * Fold ANY free-text / legacy industry value onto a canonical vertical LABEL —
 * the value stored on `industry`. Off-roster text → "Unclassified". Used by the
 * CRM forms (normalize on save), the audience builder (display), and mirrored by
 * the crm-api backfill so every layer agrees.
 */
export function canonicalizeIndustry(value: string | null | undefined): string {
  return labelForVerticalSlug(canonicalizeVerticalSlug(value));
}
